
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { MapPin, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const AddressSection = () => {
  const { profile, updateProfile } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    addressCountry: profile?.address_country || '',
    addressCity: profile?.address_city || '',
    addressArea: profile?.address_area || '',
    addressStreet: profile?.address_street || '',
    addressBuilding: profile?.address_building || '',
    addressApt: profile?.address_apt || ''
  });

  const handleSave = async () => {
    try {
      setLoading(true);
      console.log('Saving address data:', formData);
      
      await updateProfile({
        address_country: formData.addressCountry,
        address_city: formData.addressCity,
        address_area: formData.addressArea,
        address_street: formData.addressStreet,
        address_building: formData.addressBuilding,
        address_apt: formData.addressApt
      });
      
      setIsEditing(false);
      toast.success(t('address_updated', 'تم تحديث العنوان', 'Address updated successfully'));
    } catch (error) {
      console.error('Address update error:', error);
      toast.error(t('update_failed', 'فشل في التحديث', 'Update failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      addressCountry: profile?.address_country || '',
      addressCity: profile?.address_city || '',
      addressArea: profile?.address_area || '',
      addressStreet: profile?.address_street || '',
      addressBuilding: profile?.address_building || '',
      addressApt: profile?.address_apt || ''
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setFormData({
      addressCountry: profile?.address_country || '',
      addressCity: profile?.address_city || '',
      addressArea: profile?.address_area || '',
      addressStreet: profile?.address_street || '',
      addressBuilding: profile?.address_building || '',
      addressApt: profile?.address_apt || ''
    });
    setIsEditing(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            <CardTitle>{t('address_info', 'معلومات العنوان', 'Address Information')}</CardTitle>
          </div>
          {!isEditing ? (
            <Button variant="outline" size="sm" onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-2" />
              {t('edit', 'تعديل', 'Edit')}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={loading}>
                <X className="h-4 w-4 mr-2" />
                {t('cancel', 'إلغاء', 'Cancel')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? t('saving', 'جاري الحفظ...', 'Saving...') : t('save', 'حفظ', 'Save')}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="country">{t('country', 'البلد', 'Country')}</Label>
            {isEditing ? (
              <Input
                id="country"
                value={formData.addressCountry}
                onChange={(e) => setFormData({ ...formData, addressCountry: e.target.value })}
                placeholder={t('enter_country', 'أدخل البلد', 'Enter country')}
                className="mt-1"
              />
            ) : (
              <p className="font-medium mt-1">{profile?.address_country || t('not_set', 'غير محدد', 'Not set')}</p>
            )}
          </div>
          <div>
            <Label htmlFor="city">{t('city', 'المدينة', 'City')}</Label>
            {isEditing ? (
              <Input
                id="city"
                value={formData.addressCity}
                onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
                placeholder={t('enter_city', 'أدخل المدينة', 'Enter city')}
                className="mt-1"
              />
            ) : (
              <p className="font-medium mt-1">{profile?.address_city || t('not_set', 'غير محدد', 'Not set')}</p>
            )}
          </div>
          <div>
            <Label htmlFor="area">{t('area', 'المنطقة', 'Area')}</Label>
            {isEditing ? (
              <Input
                id="area"
                value={formData.addressArea}
                onChange={(e) => setFormData({ ...formData, addressArea: e.target.value })}
                placeholder={t('enter_area', 'أدخل المنطقة', 'Enter area')}
                className="mt-1"
              />
            ) : (
              <p className="font-medium mt-1">{profile?.address_area || t('not_set', 'غير محدد', 'Not set')}</p>
            )}
          </div>
          <div>
            <Label htmlFor="street">{t('street', 'الشارع', 'Street')}</Label>
            {isEditing ? (
              <Input
                id="street"
                value={formData.addressStreet}
                onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
                placeholder={t('enter_street', 'أدخل الشارع', 'Enter street')}
                className="mt-1"
              />
            ) : (
              <p className="font-medium mt-1">{profile?.address_street || t('not_set', 'غير محدد', 'Not set')}</p>
            )}
          </div>
          <div>
            <Label htmlFor="building">{t('building', 'المبنى', 'Building')}</Label>
            {isEditing ? (
              <Input
                id="building"
                value={formData.addressBuilding}
                onChange={(e) => setFormData({ ...formData, addressBuilding: e.target.value })}
                placeholder={t('enter_building', 'أدخل المبنى', 'Enter building')}
                className="mt-1"
              />
            ) : (
              <p className="font-medium mt-1">{profile?.address_building || t('not_set', 'غير محدد', 'Not set')}</p>
            )}
          </div>
          <div>
            <Label htmlFor="apartment">{t('apartment', 'الشقة', 'Apartment')}</Label>
            {isEditing ? (
              <Input
                id="apartment"
                value={formData.addressApt}
                onChange={(e) => setFormData({ ...formData, addressApt: e.target.value })}
                placeholder={t('enter_apartment', 'أدخل الشقة', 'Enter apartment')}
                className="mt-1"
              />
            ) : (
              <p className="font-medium mt-1">{profile?.address_apt || t('not_set', 'غير محدد', 'Not set')}</p>
            )}
          </div>
        </div>
        
        {!isEditing && !profile?.address_country && !profile?.address_city && !profile?.address_area && !profile?.address_street && !profile?.address_building && !profile?.address_apt && (
          <div className="text-center text-muted-foreground py-4">
            <p>{t('no_address_provided', 'لم يتم تحديد عنوان', 'No address provided')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AddressSection;
